import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Image, Newspaper, Palette, Video, Layout, CreditCard,
  Menu as MenuIcon, Award, Sparkles, Users, MessageSquare, BarChart3,
  IdCard, Gift, Mail, Plus, Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import DesignerLayout from './DesignerLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Maps template type slug → Lucide icon + pastel style (mirrors homepage)
const TYPE_META = {
  flyer:            { icon: FileText,      color: 'from-blue-500 to-cyan-500',      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50' },
  poster:           { icon: Image,         color: 'from-purple-500 to-pink-500',    bgColor: 'bg-gradient-to-br from-purple-50 to-pink-50' },
  brochure:         { icon: Newspaper,     color: 'from-green-500 to-emerald-500',  bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50' },
  logo:             { icon: Palette,       color: 'from-orange-500 to-red-500',     bgColor: 'bg-gradient-to-br from-orange-50 to-red-50' },
  youtube_thumbnail:{ icon: Video,         color: 'from-red-500 to-rose-500',       bgColor: 'bg-gradient-to-br from-red-50 to-rose-50' },
  youtube_banner:   { icon: Layout,        color: 'from-violet-500 to-purple-500',  bgColor: 'bg-gradient-to-br from-violet-50 to-purple-50' },
  business_card:    { icon: CreditCard,    color: 'from-teal-500 to-cyan-500',      bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50' },
  menu:             { icon: MenuIcon,      color: 'from-amber-500 to-yellow-500',   bgColor: 'bg-gradient-to-br from-amber-50 to-yellow-50' },
  certificate:      { icon: Award,         color: 'from-indigo-500 to-blue-500',    bgColor: 'bg-gradient-to-br from-indigo-50 to-blue-50' },
  instagram_post:   { icon: Users,         color: 'from-pink-500 to-rose-500',      bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50' },
  instagram_story:  { icon: Sparkles,      color: 'from-fuchsia-500 to-pink-500',   bgColor: 'bg-gradient-to-br from-fuchsia-50 to-pink-50' },
  facebook_post:    { icon: Users,         color: 'from-blue-600 to-blue-400',      bgColor: 'bg-gradient-to-br from-blue-50 to-sky-50' },
  meme:             { icon: MessageSquare, color: 'from-lime-500 to-green-500',     bgColor: 'bg-gradient-to-br from-lime-50 to-green-50' },
  infographic:      { icon: BarChart3,     color: 'from-sky-500 to-blue-500',       bgColor: 'bg-gradient-to-br from-sky-50 to-blue-50' },
  biodata:          { icon: IdCard,        color: 'from-emerald-500 to-teal-500',   bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50' },
  pamphlet:         { icon: FileText,      color: 'from-rose-500 to-pink-500',      bgColor: 'bg-gradient-to-br from-rose-50 to-pink-50' },
  gift_certificate: { icon: Gift,          color: 'from-red-500 to-orange-500',     bgColor: 'bg-gradient-to-br from-red-50 to-orange-50' },
  invitation:       { icon: Mail,          color: 'from-purple-500 to-violet-500',  bgColor: 'bg-gradient-to-br from-purple-50 to-violet-50' },
  presentation:     { icon: Layout,        color: 'from-indigo-500 to-violet-500',  bgColor: 'bg-gradient-to-br from-indigo-50 to-violet-50' },
  banner:           { icon: Image,         color: 'from-cyan-500 to-sky-500',       bgColor: 'bg-gradient-to-br from-cyan-50 to-sky-50' },
  email_header:     { icon: Mail,          color: 'from-slate-500 to-gray-500',     bgColor: 'bg-gradient-to-br from-slate-50 to-gray-50' },
  other:            { icon: Sparkles,      color: 'from-violet-500 to-fuchsia-500', bgColor: 'bg-gradient-to-br from-violet-50 to-fuchsia-50' },
};

const FALLBACK_META = { icon: Sparkles, color: 'from-violet-500 to-fuchsia-500', bgColor: 'bg-gradient-to-br from-violet-50 to-fuchsia-50' };

const DesignerCreatePage = () => {
  const navigate = useNavigate();
  const [templateTypes, setTemplateTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customWidth, setCustomWidth] = useState(1080);
  const [customHeight, setCustomHeight] = useState(1080);

  useEffect(() => {
    fetch(`${API_URL}/api/public/template-types`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setTemplateTypes(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openEditor = (width, height, typeId, typeName) => {
    navigate('/designer/editor', {
      state: {
        canvasSize: { width, height },
        templateTypeId: typeId || null,
        templateTypeName: typeName || null,
      },
    });
  };

  const handleTypeClick = (tt) => {
    const w = tt.width || 1080;
    const h = tt.height || 1080;
    openEditor(w, h, tt.id, tt.name);
  };

  const handleCustomCreate = () => {
    const w = parseInt(customWidth) || 1080;
    const h = parseInt(customHeight) || 1080;
    setCustomDialogOpen(false);
    openEditor(w, h, null, 'Custom Size');
  };

  // Strip " Maker" suffix if present for display, or use name as-is + add " Maker"
  const cardLabel = (tt) => `${tt.name} Maker`;

  return (
    <DesignerLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
            What Will You Design Today?
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Choose a design type to open the editor with the perfect canvas size.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {templateTypes.map((tt) => {
              const meta = TYPE_META[tt.slug] || FALLBACK_META;
              const IconComp = meta.icon;
              return (
                <Card
                  key={tt.id}
                  className="hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group border-2 border-transparent hover:border-purple-200 overflow-hidden"
                  onClick={() => handleTypeClick(tt)}
                >
                  <CardContent className={`p-6 flex flex-col items-center text-center space-y-4 ${meta.bgColor}`}>
                    <div className={`w-16 h-16 bg-gradient-to-br ${meta.color} rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-transform shadow-lg`}>
                      <IconComp className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-semibold text-sm md:text-base text-gray-800">{cardLabel(tt)}</h4>
                  </CardContent>
                </Card>
              );
            })}

            {/* Custom Size Card */}
            <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
              <DialogTrigger asChild>
                <Card className="hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group border-2 border-dashed border-purple-300 hover:border-purple-500 overflow-hidden bg-gradient-to-br from-purple-50 to-violet-50">
                  <CardContent className="p-6 flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-transform shadow-lg">
                      <Plus className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-semibold text-sm md:text-base text-gray-800">Custom Size</h4>
                  </CardContent>
                </Card>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                    Create Custom Design
                  </DialogTitle>
                  <DialogDescription>
                    Enter your custom canvas dimensions in pixels
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-6 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="custom-width" className="font-semibold">Width (px)</Label>
                    <Input
                      id="custom-width"
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(Number(e.target.value))}
                      min="100"
                      max="5000"
                      className="h-12 text-lg"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="custom-height" className="font-semibold">Height (px)</Label>
                    <Input
                      id="custom-height"
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(Number(e.target.value))}
                      min="100"
                      max="5000"
                      className="h-12 text-lg"
                    />
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Preview:</span> {customWidth} x {customHeight} pixels
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Aspect Ratio: {customHeight > 0 ? (customWidth / customHeight).toFixed(2) : '—'}:1
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleCustomCreate}
                    className="w-full h-12 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 font-semibold text-lg"
                  >
                    Create Design
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </DesignerLayout>
  );
};

export default DesignerCreatePage;
